import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { PageHero, Section } from "../components/public/PublicLayout";

const HERO = "https://images.pexels.com/photos/35655151/pexels-photo-35655151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600";

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) { toast.error("Please fill name, phone and message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHero title="Contact Us" subtitle="We would love to hear from you" image={HERO} />
      <Section className="grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">Temple Office</h2>
          <ul className="space-y-4 text-stone-700">
            <li className="flex gap-3"><MapPin className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" /> {s.address}</li>
            <li className="flex gap-3"><Phone className="w-5 h-5 text-orange-600 shrink-0" /> {s.phone}</li>
            <li className="flex gap-3"><Mail className="w-5 h-5 text-orange-600 shrink-0" /> {s.email}</li>
            <li className="flex gap-3"><Clock className="w-5 h-5 text-orange-600 shrink-0" /> Open daily · see Darshan & Timings</li>
          </ul>
          <div className="rounded-2xl overflow-hidden shadow-lg h-64 border border-amber-200 mt-6">
            <iframe title="map" src={s.maps_embed_url} className="w-full h-full" loading="lazy" />
          </div>
          <a href={s.maps_directions_url} target="_blank" rel="noreferrer" className="inline-block mt-4 bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-full transition-colors" data-testid="contact-directions">Get Directions</a>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm h-fit" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">Send a Message</h2>
          <div className="space-y-4">
            <input placeholder="Your Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="contact-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="contact-phone" />
              <input placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="contact-email" />
            </div>
            <input placeholder="Subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="contact-subject" />
            <textarea placeholder="Your Message *" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="contact-message" />
            <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-full disabled:opacity-40 transition-colors" data-testid="contact-submit">
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Message"}
            </button>
          </div>
        </form>
      </Section>
    </div>
  );
}
